import { supabase } from '../lib/supabase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj.id,
    supplierName: obj.supplier_name,
    supplierType: obj.supplier_type,
    apiKey: obj.api_key,
    secretKey: obj.secret_key,
    testApiKey: obj.test_api_key,
    testSecretKey: obj.test_secret_key,
    apiEndpointLive: obj.api_endpoint_live,
    apiEndpointTest: obj.api_endpoint_test,
    isLive: obj.is_live,
    isActive: obj.is_active,
    manufacturerId: obj.manufacturer_id,
    emailNotificationsEnabled: obj.email_notifications_enabled !== false,
    apiIntegrationEnabled: obj.api_integration_enabled === true,
    adminDeactivated: obj.admin_deactivated,
    payloadTemplates: obj.payload_templates,
    capabilities: obj.capabilities || { machines: [], materials: [] },
    createdAt: obj.created_at,
    updatedAt: obj.updated_at
  };
};

const toSnakeCase = (obj) => {
  return {
    supplier_name: obj.supplierName,
    supplier_type: obj.supplierType,
    api_key: obj.apiKey,
    secret_key: obj.secretKey,
    test_api_key: obj.testApiKey,
    test_secret_key: obj.testSecretKey,
    api_endpoint_live: obj.apiEndpointLive,
    api_endpoint_test: obj.apiEndpointTest,
    is_live: obj.isLive,
    is_active: obj.isActive,
    manufacturer_id: obj.manufacturerId === '' ? null : obj.manufacturerId,
    email_notifications_enabled: obj.emailNotificationsEnabled,
    api_integration_enabled: obj.apiIntegrationEnabled,
    admin_deactivated: obj.adminDeactivated,
    payload_templates: obj.payloadTemplates,
    capabilities: obj.capabilities
  };
};

export const manufacturingService = {
  /**
   * Get manufacturer account for current user
   */
  async getMyAccount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('manufacturer_accounts')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get manufacturing config for current manufacturer
   */
  async getMyConfig() {
    const account = await this.getMyAccount();
    if (!account) return null;

    const { data, error } = await supabase
      .from('manufacturing_supplier_configs')
      .select('*')
      .eq('manufacturer_id', account.id)
      .maybeSingle();
    
    if (error) throw error;
    return data ? toCamelCase(data) : null;
  },

  /**
   * Update or create my config
   */
  async saveMyConfig(config) {
    const account = await this.getMyAccount();
    if (!account) throw new Error('Manufacturer account not found');

    const payload = toSnakeCase({
      ...config,
      manufacturerId: account.id
    });

    let result;
    if (config.id) {
      result = await supabase
        .from('manufacturing_supplier_configs')
        .update(payload)
        .eq('id', config.id)
        .select()
        .single();
    } else {
      result = await supabase
        .from('manufacturing_supplier_configs')
        .insert(payload)
        .select()
        .single();
    }

    if (result.error) throw result.error;
    return toCamelCase(result.data);
  },

  /**
   * Request a 3D print quote from the backend
   * @param {string} componentId - ID of the component to print
   * @param {number} quantity - Number of units
   * @param {string} material - Selected material (e.g., 'PLA', 'PETG')
   * @returns {Promise<Object>} - Quote details
   */
  async getQuote(componentId, quantity = 1, material = 'PLA') {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required to request a quote');
    }

    let finalComponentId = componentId;
    let finalQuantity = quantity;
    let finalMaterial = material;

    if (componentId && typeof componentId === 'object') {
      finalComponentId = componentId.componentId || componentId.productId;
      finalQuantity = componentId.quantity ?? 1;
      finalMaterial = componentId.material || 'PLA';
    }

    const response = await fetch(`${API_BASE_URL}/manufacturing/quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        component_id: finalComponentId,
        quantity: finalQuantity,
        material: finalMaterial
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.detail || 'Failed to retrieve manufacturing quote');
    }

    return result;
  },

  /**
   * Get all supported materials from the platform library
   */
  async getMaterials(full = false) {
    const { data, error } = await supabase
      .from('manufacturing_materials')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching materials from library:', error);
      return [];
    }
    
    if (full) return data;
    return data.map(m => m.name);
  },

  /**
   * Get dynamic details about manufacturer subscription tiers
   */
  getTierInfo(tier) {
    const tiers = {
      manufacturers_view_only: {
        name: 'Complimentary Viewer',
        price: '0.00',
        features: [
          'View-only access to custom orders',
          'View-only access to material library',
          'View-only access to machine telemetry',
          'Symmetrical visualizer is read-only',
          'Order manufacturing bidding is paused'
        ]
      },
      workshop: {
        name: 'The Workshop',
        price: '15.00',
        features: [
          'Up to 2 active machines',
          'Email order notifications',
          'Standard FDM/SLA materials',
          'Basic dashboard analytics'
        ]
      },
      factory: {
        name: 'The Factory',
        price: '25.00',
        features: [
          'Unlimited active machines (3D/CNC)',
          'Includes all "Workshop" features',
          'Priority queue routing on catalog',
          'Symmetrical directory badge'
        ]
      },
      foundry: {
        name: 'The Foundry',
        price: '59.00',
        features: [
          'Symmetrical webhooks & API access',
          'Includes all "Factory" features',
          'White-glove account manager',
          'Custom contract queue matching'
        ]
      }
    };
    return tiers[tier] || tiers.workshop;
  },

  /**
   * Update subscription tier of a manufacturer account
   */
  async changeTier(accountId, tier) {
    let monthlyServiceFee = 15.0;
    try {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'subscription_plans')
        .maybeSingle();
      if (data && data.value && data.value.manufacturer && data.value.manufacturer[tier]) {
        monthlyServiceFee = parseFloat(data.value.manufacturer[tier].price) || 15.0;
      } else {
        const feeMap = { workshop: 15.0, factory: 25.0, foundry: 59.0 };
        monthlyServiceFee = feeMap[tier] || 15.0;
      }
    } catch (e) {
      const feeMap = { workshop: 15.0, factory: 25.0, foundry: 59.0 };
      monthlyServiceFee = feeMap[tier] || 15.0;
    }

    const { data, error } = await supabase
      .from('manufacturer_accounts')
      .update({ 
        tier,
        monthly_service_fee: monthlyServiceFee,
        updated_at: new Date().toISOString()
      })
      .eq('id', accountId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

};
